/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgProfileService } from '@/services/organization/org-profile.service';
import { OrganizationProfile, UpdateOrgProfileRequest } from '@/types/superadmin.types';
import { ApiError } from '@/services/api/types';
import { useAuthStore } from '@/stores/authStore';

export const orgProfileKeys = {
  all: ['org-profile'] as const,
  detail: () => [...orgProfileKeys.all, 'detail'] as const,
};

export function useOrgProfile() {
  const { user } = useAuthStore();
  const isOrgUser = !!user && user.role !== 'SUPER_ADMIN' && !!user.orgId;

  return useQuery<OrganizationProfile, ApiError>({
    queryKey: orgProfileKeys.detail(),
    queryFn: orgProfileService.getProfile,
    enabled: isOrgUser,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateOrgProfile() {
  const queryClient = useQueryClient();

  return useMutation<OrganizationProfile, ApiError, UpdateOrgProfileRequest>({
    mutationFn: (data) => orgProfileService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgProfileKeys.all });
    },
  });
}
